import { FetchClient, type RequestOptions } from '@foundatiofx/fetchclient';
import type { PagedResult } from '#api/common.ts';

type GetRequestOptions = NonNullable<Parameters<FetchClient['getJSON']>[1]>;

export interface GenericApiOptions {
    fetch?: FetchClient;
    get?: GetRequestOptions;
    list?: GetRequestOptions;
    create?: RequestOptions;
    update?: RequestOptions;
    delete?: RequestOptions;
}

export class GenericApi<
    TEntity,
    TCreate extends object,
    TUpdate extends object,
    TList extends Record<string, unknown>,
    TId = number
> {
    protected fetch: FetchClient;
    protected options: Omit<GenericApiOptions, 'fetch'>;

    constructor(
        protected route: string,
        options: GenericApiOptions | FetchClient = {}
    ) {
        const { fetch, ...rest } = options instanceof FetchClient ? { fetch: options } : options;
        this.fetch = fetch ?? new FetchClient();
        this.options = rest;
    }

    async get(id: TId, options?: GetRequestOptions) {
        return await this.fetch.getJSON<TEntity>(`${this.route}/${id}`, { ...this.options.get, ...options });
    }

    async list(params: TList) {
        return await this.fetch.getJSON<PagedResult<TEntity>>(this.route, { ...this.options.list, params });
    }

    async create(payload: TCreate, options?: RequestOptions) {
        return await this.fetch.postJSON<TEntity>(this.route, payload, { ...this.options.create, ...options });
    }

    async update(id: TId, payload: TUpdate, options?: RequestOptions) {
        return await this.fetch.putJSON<TEntity>(`${this.route}/${id}`, payload, {
            ...this.options.update,
            ...options
        });
    }

    async delete(id: TId, options?: RequestOptions) {
        return await this.fetch.delete(`${this.route}/${id}`, { ...this.options.delete, ...options });
    }
}
